import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, of } from 'rxjs';
import {
    BusinessCustomerFeature,
    CampaignFeature,
    DealerFeature,
    HomeFeature,
    HyundaiPromiseFeature,
    HyundaiPromiseVMFeature,
    ModelsFeature,
    ModelsVMFeature,
    ServicesFeature,
    VehicleFeature,
    WarrantyPageFeature,
} from '..';
import { isHWS } from '../../providers/platform.token';
import { actions } from './footnotes.actions';

@Injectable()
export class FootnotesEffects {
    protected readonly actions$ = inject(Actions);
    protected readonly store = inject(Store);
    protected readonly isHWS = isHWS();

    setModelListFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ModelsFeature.actions.loadModelListSuccess),
            map(({ modelList }) => actions.addFootnotes({ footnotes: modelList.footnotes })),
        );
    });

    setModelListVMFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ModelsVMFeature.actions.loadModelVMListSuccess),
            map(({ modelList }) => actions.addFootnotes({ footnotes: modelList.footnotes })),
        );
    });

    setModelFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ModelsFeature.actions.loadModelDetailSuccess),
            map(({ model }) => actions.addFootnotes({ footnotes: model.footnotes })),
        );
    });

    setHomeFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HomeFeature.actions.loadHomeSuccess),
            concatLatestFrom(() => (this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null))),
            map(([{ home }, dealer]) => {
                return actions.addFootnotes({
                    footnotes: dealer ? [...dealer.footnotes, ...home.footnotes] : home.footnotes,
                });
            }),
        );
    });

    setCampaignOverviewFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CampaignFeature.actions.loadCampaignOverviewSuccess),
            map(({ campaignsOverview }) => actions.addFootnotes({ footnotes: campaignsOverview.footnotes })),
        );
    });

    setCampaignDetailFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CampaignFeature.actions.loadCampaignDetailSuccess),
            map(({ campaign }) => actions.addFootnotes({ footnotes: campaign.footnotes })),
        );
    });

    setWarrantyPageFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WarrantyPageFeature.actions.loadPageWarrantySuccess),
            map(({ content }) => actions.addFootnotes({ footnotes: content.footnotes })),
        );
    });

    setDetailPageFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VehicleFeature.vehicleActions.loadVehicleDetailSuccess),
            map(({ vehicle }) => actions.addFootnotes({ footnotes: vehicle.footnotes })),
        );
    });

    setHyundaiPromisePageFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HyundaiPromiseFeature.actions.loadHyundaiPromiseSuccess),
            map(({ content }) => actions.addFootnotes({ footnotes: content.footnotes })),
        );
    });

    setHyundaiPromisePageVMFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HyundaiPromiseVMFeature.actions.loadHyundaiPromiseVMSuccess),
            map(({ content }) => actions.addFootnotes({ footnotes: content.footnotes })),
        );
    });

    setBusinessCustomerPageFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BusinessCustomerFeature.actions.loadPageBusinessCustomerSuccess),
            map(({ content }) => actions.addFootnotes({ footnotes: content.footnotes })),
        );
    });

    setHeroServicesFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ServicesFeature.actions.loadServicesHeroSuccess),
            map(({ servicesHero }) => actions.addFootnotes({ footnotes: servicesHero.footnotes })),
        );
    });

    setGeneralServicesFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ServicesFeature.actions.loadGeneralServicesSuccess),
            map(({ generalServices }) => actions.addFootnotes({ footnotes: generalServices.footnotes })),
        );
    });

    setServiceFormFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ServicesFeature.actions.loadServiceFormSuccess),
            map(({ serviceFormContent }) => actions.addFootnotes({ footnotes: serviceFormContent.footnotes })),
        );
    });

    setDealerCampaignFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CampaignFeature.actions.loadDealerCampaignDetailSuccess),
            map((res) => actions.addFootnotes({ footnotes: res.footnotes })),
        );
    });

    setHighlightBannerFootnotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DealerFeature.actions.loadDealerBySlugSuccess),
            map((res) => actions.addFootnotes({ footnotes: res.dealer.highlightBanner?.footnotes ?? [] })),
        );
    });
}
